import React from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { DotNavigation, IconButton, Text } from 'wix-ui-tpa/cssVars';
import ChevronLeft from 'wix-ui-icons-common/on-stage/ChevronLeftSmall';
import ChevronRight from 'wix-ui-icons-common/on-stage/ChevronRightSmall';
import { classes } from './ParticipantsPager.st.css';
import { ParticipantsPagerDataHooks } from './dataHooks';

export interface ParticipantsPagerProps {
  currentIndex: number;
  total: number;
  onSelect: (index: number) => void;
  children: React.ReactNode;
}

const ParticipantsPager: React.FC<ParticipantsPagerProps> = ({
  currentIndex,
  total,
  onSelect,
  children,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useEnvironment();

  const showPager = total > 1;

  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= total - 1;

  const goPrev = () => !isFirst && onSelect(currentIndex - 1);
  const goNext = () => !isLast && onSelect(currentIndex + 1);

  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div
      className={classes.root}
      data-hook={ParticipantsPagerDataHooks.ROOT}
    >
      {showPager && (
        <>
          <div className={classes.navigation}>
            <IconButton
              data-hook={ParticipantsPagerDataHooks.PREV_BUTTON}
              icon={<PrevIcon aria-hidden />}
              disabled={isFirst}
              onClick={goPrev}
              aria-label={t('bookings.form.collectParticipant.pager.previous')}
            />
            <Text
              className={classes.label}
              data-hook={ParticipantsPagerDataHooks.LABEL}
            >
              {t('bookings.form.collectParticipant.pager.label', {
                current: currentIndex + 1,
                total,
              })}
            </Text>
            <IconButton
              data-hook={ParticipantsPagerDataHooks.NEXT_BUTTON}
              icon={<NextIcon aria-hidden />}
              disabled={isLast}
              onClick={goNext}
              aria-label={t('bookings.form.collectParticipant.pager.next')}
            />
          </div>
          <div className={classes.dots}>
            <DotNavigation
              data-hook={ParticipantsPagerDataHooks.DOT_NAVIGATION}
              currentIndex={currentIndex}
              length={total}
              onSelect={onSelect}
              aria-label={t('bookings.form.collectParticipant.pager.label', {
                current: currentIndex + 1,
                total,
              })}
            />
          </div>
        </>
      )}
      {children}
    </div>
  );
};

export default ParticipantsPager;
